import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { formatDateTime, formatCurrency } from '../utils/format';
import { useForm, Controller } from 'react-hook-form';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import TableView from '../components/TableView';
import LabelValueItem from '../components/LabelValueItem';
import PurchaseSupplierInfo from '../components/PurchaseSupplierInfo';
import PurchaseWarehouseChangeModal from '../components/PurchaseWarehouseChangeModal';
import {
  Grid, Box, Typography, Button, TextField, InputAdornment, Tooltip,
  Stack, Link, Divider, Paper, IconButton, Alert, MenuItem
} from '@mui/material';
import { Add, SupervisorAccount, Delete, Sync, HelpOutline, Edit, Close } from '@mui/icons-material';
import PdfPrintButton from '../components/PdfPrintButton';
import InputFieldAddress from '../components/InputFieldAddress';
import usePurchaseTable from '../hooks/usePurchaseTable';

const PurchaseDetailPage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [purchaseData, setPurchaseData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    items: []
  });
  const { register, handleSubmit, control, trigger, getValues, formState: { errors }, setValue, reset } = useForm({
    defaultValues: purchaseData,
  });
  const handleItemDelete = async (index, item_id) => {
    const values = getValues();

    // 新規行（バリデーション・confirmなし）
    if (!item_id) {
      const updatedItems = values.items.filter((_, i) => i !== index);

      const { total_amount, total_amount_including_tax } =
        calculateTotalAmount(updatedItems);

      setPurchaseData(prev => ({
        ...prev,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
      }));

      setValue('items', updatedItems);

      return;
    }
    // バリデーション実行
    const valid = await trigger(); // 全フィールドをチェック
    if (!valid) {
      showSnackbar('入力内容に不備があります。削除できません', 'error');
      return;
    }

    const ok = window.confirm(
      'この明細を削除します。よろしいですか？\n\n' +
      '※　発注を取り消す場合は「発注キャンセル」ボタンを使用してください。'
    );
    if (!ok) return;

    try {
      // 削除対象を除いた items 配列を作る
      const updatedItems = values.items.filter(item => item.item_id !== item_id);
      // 合計計算
      const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);

      // サーバー送信用データ
      const submitData = {
        ...purchaseData,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
        delete_item_id: item_id,
      };

      // DB 更新
      const res = await axiosInstance.put(`/purchase/${id}/update`, { purchaseData: submitData });

      showSnackbar(res?.data?.message || '発注アイテムを削除しました', 'success');

      fetchPurchase();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || 'この発注は確定済みのため、削除できません', 'error');
    }
  };
  const {
    columns,
    updateItem,
    handleItemChange,
  } = usePurchaseTable({
    control,
    errors,
    isEditMode,
    hasLimit,
    axiosInstance,
    selectedSupplier,
    purchaseData,
    setPurchaseData,
    calculateSellingPriceIncludingTax,
    calculateItemSubtotal,
    calculateTotalAmount,
    formatCurrency,
    handleItemDelete,
  });
  const fetchPurchase = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/purchase/${id}`);
      setPurchaseData(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      setError(err?.response?.data?.message || '取得に失敗しました');
      setPurchaseData(null);
    }
  }, [id, showSnackbar]);
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await axiosInstance.get('/warehouse', {
          params: { activeOnly: true }
        });
        setWarehouses(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(err);
        }
      }
    };
    fetchWarehouses();
  }, []);
  useEffect(() => {
    fetchPurchase();
  }, [fetchPurchase]);

  useEffect(() => {
    if (!purchaseData) return;

    const formattedItems = (purchaseData.items || []).map(item => ({
      ...item,
      price: Math.floor(item.price),          // 定価を整数化
      tax_rate: Math.floor(item.tax_rate),    // 税率も整数
    }));
    reset({
      ...purchaseData,
      items: formattedItems,
      warehouse_id: purchaseData.warehouse_id,
    });
    setSelectedSupplier({ supplier_id: purchaseData.supplier_id });
  }, [purchaseData, reset]);

  useEffect(() => {
    const snackbarState = location.state?.snackbar;
    if (snackbarState) {
      showSnackbar(snackbarState.message, snackbarState.severity);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, showSnackbar]);
  if (!purchaseData) return null;
  const addItem = () => {
    const newItem = {
      product_id: null,
      variant_id: null,
      sku: '',
      quantity: 1,
      purchase_price: 0,
      purchase_price_including_tax: 0,
      tax_rate: 0,
      subtotal: 0,
      subtotal_including_tax: 0,
    };
    const newItems = [...(purchaseData.items ?? []), newItem];

    setPurchaseData({
      ...purchaseData,
      items: newItems,
    });
    setValue(`items.${newItems.length - 1}`, newItem);
  };
  const updateStatus = async (statusId) => {
    const ok = window.confirm('ステータスを変更します。\nこの操作は取り消せません。よろしいですか？');
    if (!ok) return;
    try {
      const res = await axiosInstance.put(`/purchase/${id}/update-status`, { status_id: statusId });
      fetchPurchase();
      showSnackbar(res?.data?.message || '発注情報が更新されました', 'success');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };
  const onSubmit = async (data) => {
    const submitData = {
      ...purchaseData,
      ...data,
      purchase_date: null,
      items: purchaseData.items,
      total_amount: purchaseData.total_amount,
      total_amount_including_tax: purchaseData.total_amount_including_tax
    };
    try {
      const res = await axiosInstance.put(`/purchase/${id}/update`, { purchaseData: submitData });
      showSnackbar(res?.data?.message || '発注情報が更新されました', 'success');
      fetchPurchase();
      setIsEditMode(false);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };
  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant="h1" component="h1">発注詳細</Typography>
      </Stack>
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Paper variant='section'>
          <Controller
            name="warehouse_id"
            control={control}
            rules={{ required: "倉庫を選択してください" }}
            render={({ field }) => (
              <TextField
                select
                label="入庫倉庫"
                {...field}
                value={field.value ?? ''}
                disabled={!isEditMode}
                error={!!errors.warehouse_id}
                helperText={errors.warehouse_id?.message}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  field.onChange(value);

                  const selected = warehouses.find(w => Number(w.warehouse_id) === Number(value));
                  if (selected) {
                    setValue('postal_code', selected.postal_code || '', { shouldDirty: true });
                    setValue('state', selected.state || '', { shouldDirty: true });
                    setValue('address_line', selected.address_line || '', { shouldDirty: true });
                  }
                }}
              >
                {warehouses.map((wh) => (
                  <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                    {wh.warehouse_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Paper>
        <Paper variant='section'>
          {purchaseData?.status_id && (
            <Alert
              severity={
                purchaseData.status_id === '4' ? 'success' :  // 成約
                  purchaseData.status_id === '5' ? 'error' :    // 失注
                    'info'                                                  // 作成中・提出済
              }
              sx={{ mb: 2 }}
            >
              {purchaseData.status_name}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><LabelValueItem label='発注日時' value={formatDateTime(purchaseData.date)} /></Grid>
            <Grid item xs={12} sm={6}><LabelValueItem label='発注番号' value={purchaseData.number} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography component='h2' variant="h4" mb={2}>発注先情報</Typography>
            {hasLimit('max_suppliers') && purchaseData.supplier_id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SupervisorAccount />}
                onClick={() => navigate(`/suppliers/${purchaseData.supplier_id}`)}
              >
                仕入先情報を確認
              </Button>
            )}
          </Stack>
          <PurchaseSupplierInfo
            hasLimit={hasLimit}
            isEditMode={isEditMode}
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            setSelectedSupplier={setSelectedSupplier}
          />
          <Divider sx={{ my: 2 }} />
          <Stack direction='row' alignItems='center' mb={2}>
            <Typography component='h2' variant="h4" sx={{ mb: 0 }}>納入先情報</Typography>
            <Tooltip
              title={
                <Typography variant="body2">
                  倉庫管理で倉庫の住所を保存すると、自動入力できます。
                </Typography>
              }
              arrow
              placement="right"
            >
              <IconButton size="small" aria-label="help">
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <InputFieldAddress
            register={register}
            errors={errors}
            isEditMode={isEditMode}
            setValue={setValue}
            control={control}
            label='納入先住所'
          />
          <Divider sx={{ my: 2 }} />
          <TextField
            label="備考"
            multiline
            minRows={3}
            disabled={!isEditMode}
            error={!!errors.notes}
            helperText={errors.notes?.message}
            {...register('notes', {
              maxLength: {
                value: 2000,
                message: '2000文字以内で入力してください',
              },
            })}
            inputProps={{ maxLength: 2000 }}
            sx={{ mt: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Stack direction='row' alignItems='center' mb={2}>
            <Typography component='h2' variant="h4" sx={{ mb: 0 }}>商品情報</Typography>
            <Tooltip
              title={
                <Typography variant="body2">
                  仕入先一覧で取扱い商品を紐づけてください
                </Typography>
              }
              arrow
              placement="right"
            >
              <IconButton size="small" aria-label="help">
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <TableView dataset={purchaseData.items ?? []} columns={columns} />
          <Button
            type="button"
            variant="outlined"
            startIcon={<Add />}
            onClick={addItem}
            disabled={!isEditMode}
            size='small'
            sx={{ mt: 1 }}
          >
            アイテムを追加
          </Button>
          {/* 合計金額 */}
          <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight="bold">合計金額（税抜）</Typography>
                <Typography fontWeight="bold">{formatCurrency(purchaseData.total_amount)}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight="bold">合計金額（税込）</Typography>
                <Typography fontSize={22} fontWeight="bold">{formatCurrency(purchaseData.total_amount_including_tax)}</Typography>
              </Stack>
              <Divider />
            </Stack>
          </Box>

          <div className='button-row'>
            {['1'].includes(purchaseData.status_id) && (
              <>
                <Button
                  variant='outlined'
                  startIcon={isEditMode ? <Close /> : <Edit />}
                  onClick={async () => {
                    if (isEditMode) {
                      await fetchPurchase();
                    }
                    setIsEditMode(prev => !prev);
                  }}
                >
                  {isEditMode ? '編集を破棄する' : 'この発注を編集'}
                </Button>
                {isEditMode &&
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Sync />}
                  >
                    内容を更新する
                  </Button>
                }
              </>
            )}
            {purchaseData?.status_id === '1' && !isEditMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => updateStatus(2)}
              >
                送付済み
              </Button>
            )}
            {purchaseData?.status_id === '2' && !isEditMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => updateStatus(3)}
              >
                入庫待ち
              </Button>
            )}
            {['3'].includes(purchaseData.status_id) && !isEditMode && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => setWarehouseModalOpen(true)}
                >
                  倉庫変更
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={async () => {
                    const warehouseId = purchaseData.warehouse_id;
                    const warehouseName = warehouses.find(w => w.warehouse_id === warehouseId)?.warehouse_name || '';

                    if (!warehouseId) {
                      showSnackbar('入庫する倉庫を選択してください', 'warning');
                      return;
                    }

                    const confirm = window.confirm(`「${warehouseName}」に入庫します。よろしいですか？\n※この操作は取り消せません。`);
                    if (!confirm) return;

                    try {
                      const res = await axiosInstance.post(`/purchase/${id}/inventory-insert`);

                      showSnackbar(res.data?.message || '入庫が完了しました', res.data?.severity || 'success');
                      fetchPurchase();
                    } catch (err) {
                      console.error(err);
                      showSnackbar(err?.response?.data?.message || '入庫に失敗しました', 'error');
                    }
                  }}
                >
                  入庫する
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  disabled={purchaseData.status_id === '4'}
                  onClick={() => updateStatus(5)}
                >
                  取消
                </Button>
              </>
            )}
          </div>
          <Divider sx={{ my: 4 }} />
          <Alert severity="info" icon={<HelpOutline />}>
            帳票に記載される自社情報は「会社設定」で設定できます。
            <Link
              component="button"
              onClick={() => navigate('/setting/invoice')}
              underline="none"
              sx={{ ml: 2 }}
            >
              （会社設定）
            </Link>
          </Alert>
          <div className='button-row'>
            {hasLimit('invoices_and_delivery') &&
              <PdfPrintButton
                id={purchaseData.id}
                isEditMode={isEditMode}
                pdfType="purchase" label="発注書"
              />
            }
            {!isEditMode &&
              <Button
                variant="outlined"
                color="primary"
                sx={{ ml: 1 }}
                size='small'
                onClick={async () => {
                  try {
                    const res = await axiosInstance.post(`/purchase/${id}/copy`);
                    navigate(`/purchases/${res.data.purchaseId}`, {
                      state: { snackbar: { message: res?.data?.message || '発注をコピーして再作成しました。', severity: 'success' } }
                    });
                  } catch (err) {
                    if (process.env.NODE_ENV === 'development') {
                      console.error(err);
                    }
                    showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
                  }
                }}
              >
                コピーして再作成
              </Button>
            }
          </div>
        </Paper >
      </form>
      <PurchaseWarehouseChangeModal
        open={warehouseModalOpen}
        onClose={() => setWarehouseModalOpen(false)}
        purchaseId={purchaseData.id}
        currentWarehouse={{
          warehouse_id: purchaseData.warehouse_id,
          warehouse_name: purchaseData.warehouse_name
        }}
        warehouses={warehouses}
        showSnackbar={showSnackbar}
        onSuccess={fetchPurchase}
      />
    </>
  );
};

export default PurchaseDetailPage;
